/**
 * Common component to render a system note, icon and user information.
 *
 * This component needs to be used with a vuex store.
 * That vuex store needs to have a `targetNoteHash` getter
 *
 * @example
 * <system-note
 *   :note="{
 *     id: String,
 *     author: Object,
 *     createdAt: String,
 *     note_html: String,
 *     system_note_icon_name: String
 *    }"
 *   />
 */ import { GlButton, GlSkeletonLoader, GlTooltipDirective, GlIcon } from '@gitlab/ui';
import $ from 'jquery';
import { mapActions, mapState } from 'pinia';
import SafeHtml from '~/vue_shared/directives/safe_html';
import descriptionVersionHistoryMixin from 'ee_else_ce/notes/mixins/description_version_history';
import axios from '~/lib/utils/axios_utils';
import { __ } from '~/locale';
import NoteHeader from '~/notes/components/note_header.vue';
import { renderGFM } from '~/behaviors/markdown/render_gfm';
import { useNotes } from '~/notes/store/legacy_notes';
import TimelineEntryItem from './timeline_entry_item.vue';
const MAX_VISIBLE_COMMIT_LIST_COUNT = 3;
const ICON_COLORS = {
    check: 'system-note-icon-success',
    'merge-request-close': 'system-note-icon-danger',
    merge: 'system-note-icon-info',
    'issue-close': 'system-note-icon-info',
    'work-item-issue': 'system-note-icon-success',
    error: 'system-note-icon-danger',
    'review-warning': 'system-note-icon-warning',
    'comment-lines': 'system-note-icon-info'
};
export default {
    i18n: {
        deleteButtonLabel: __('Remove description history')
    },
    name: 'SystemNote',
    components: {
        GlIcon,
        NoteHeader,
        TimelineEntryItem,
        GlButton,
        GlSkeletonLoader
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        descriptionVersionHistoryMixin
    ],
    props: {
        note: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            expanded: false,
            lines: [],
            showLines: false,
            loadingDiff: false
        };
    },
    computed: {
        ...mapState(useNotes, [
            'targetNoteHash',
            'descriptionVersions',
            'isLoadingDescriptionVersion'
        ]),
        noteAnchorId () {
            return `note_${this.note.id}`;
        },
        isAllowedIcon () {
            return Object.keys(ICON_COLORS).includes(this.note.system_note_icon_name);
        },
        isTargetNote () {
            return this.targetNoteHash === this.noteAnchorId;
        },
        toggleIcon () {
            return this.expanded ? 'chevron-up' : 'chevron-down';
        },
        // following 2 methods taken from code in `collapseLongCommitList` of notes.js:
        actionTextHtml () {
            return $(this.note.note_html).unwrap().html();
        },
        hasMoreCommits () {
            return $(this.note.note_html).filter('ul').children().length > MAX_VISIBLE_COMMIT_LIST_COUNT;
        },
        descriptionVersion () {
            return this.descriptionVersions[this.note.description_version_id];
        },
        singleLineDescription () {
            return !this.descriptionVersion?.match(/\n/g);
        },
        deleteButtonClasses () {
            return this.singleLineDescription ? 'gl-top-5 gl-right-2 gl-mt-2' : 'gl-top-6 gl-right-3';
        },
        iconBgClass () {
            return ICON_COLORS[this.note.system_note_icon_name] || 'gl-text-subtle';
        },
        systemNoteIconName () {
            let icon = this.note.system_note_icon_name;
            if (this.note.system_note_icon_name === 'work-item-issue') {
                // eslint-disable-next-line @gitlab/require-i18n-strings
                if (this.note.noteable_type === 'Issue') {
                    icon = 'issue-open-m';
                } else if (this.note.noteable_type === 'MergeRequest') {
                    icon = 'merge-request-open';
                }
            }
            return icon;
        }
    },
    mounted () {
        renderGFM(this.$refs['gfm-content']);
    },
    methods: {
        // eslint-disable-next-line vue/no-unused-properties -- These are used by the `descriptionVersionHistoryMixin` mixin
        ...mapActions(useNotes, [
            'fetchDescriptionVersion',
            'softDeleteDescriptionVersion'
        ]),
        async toggleDiff () {
            this.showLines = !this.showLines;
            if (!this.lines.length) {
                this.loadingDiff = true;
                const { data } = await axios.get(this.note.outdated_line_change_path);
                this.lines = data.map((l)=>({
                        ...l,
                        rich_text: l.rich_text.replace(/^[+ -]/, '')
                    }));
                this.loadingDiff = false;
            }
        }
    }
};
